import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { PollAnswer, PublicPollDto } from '@tww/shared';
import { fmtDate } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import logo from '../../assets/tww-logo.svg';
import { TextArea } from '../../ui/fields';
import { daysBetween, MonthCalendar } from '../../ui/MonthCalendar';
import '../../styles/polls.css';

/**
 * Öffentliche Abstimmungsseite über den persönlichen Link: ohne Anmeldung, Anrede „Sie“, nur eigene Antworten sichtbar.
 * Die vorgeschlagenen Tage stehen im Kalender. Klick auf einen Tag: möglich (grün), zweiter Klick: bedingt möglich (orange),
 * dritter Klick: Auswahl aufheben. Nicht markierte vorgeschlagene Tage werden als „nicht möglich“ gespeichert.
 */
export function PublicPollPage() {
  const { token = '' } = useParams();
  const q = useQuery({ queryKey: ['public-poll', token], queryFn: () => api.get<PublicPollDto>(`/polls/${token}`), retry: false });
  const [picked, setPicked] = useState<Record<string, 'yes' | 'maybe'>>({});
  const [comment, setComment] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!q.data) return;
    setPicked(Object.fromEntries(Object.entries(q.data.answers).filter(([, v]) => v === 'yes' || v === 'maybe')) as Record<string, 'yes' | 'maybe'>);
    setComment(q.data.comment ?? '');
  }, [q.data]);
  const send = useMutation({
    mutationFn: () => {
      const answers: Record<string, PollAnswer> = Object.fromEntries(q.data!.dates.map((d) => [d, picked[d] ?? 'no']));
      return api.post<PublicPollDto>(`/polls/${token}`, { answers, comment });
    },
    onSuccess: () => setSaved(true),
  });
  const p = q.data;
  const offered = new Set(p?.dates ?? []);
  // Klick-Zyklus: möglich → bedingt möglich → keine Auswahl
  const cycle = (d: string) =>
    setPicked((x) => {
      const n = { ...x };
      if (!x[d]) n[d] = 'yes';
      else if (x[d] === 'yes') n[d] = 'maybe';
      else delete n[d];
      return n;
    });
  // Ziehen über mehrere Tage: vom markierten Tag aus Auswahl aufheben, sonst als möglich markieren
  const range = (from: string, to: string, anchor: string) =>
    setPicked((x) => {
      const n = { ...x };
      for (const d of daysBetween(from, to).filter((y) => offered.has(y))) {
        if (x[anchor]) delete n[d];
        else n[d] = 'yes';
      }
      return n;
    });
  const firstMonth = p?.dates[0]?.slice(0, 7) ?? '';
  const lastMonth = p?.dates[p.dates.length - 1]?.slice(0, 7) ?? '';
  const anyPicked = Object.keys(picked).length > 0;
  return (
    <div className="login poll-page">
      <div className="card">
        <div className="brand login-logo">
          <img src={logo} alt="Trinkwasserwald e.V." />
        </div>
        {q.isLoading ? (
          <div className="skeleton" style={{ width: 240, height: 20 }} />
        ) : q.isError || !p ? (
          <div className="note bad">{q.error instanceof ApiRequestError && q.error.status === 404 ? 'Dieser Link ist nicht (mehr) gültig.' : 'Die Abstimmung konnte nicht geladen werden.'}</div>
        ) : (
          <>
            <div>
              <h1 className="poll-title">Terminabstimmung: {p.title}</h1>
              <p className="login-lead">
                {p.place ? `${p.place} · ` : ''}für {p.participant.name}{p.participant.role_label ? ` (${p.participant.role_label})` : ''}
              </p>
            </div>
            {p.message && <div className="note info poll-message">{p.message}</div>}
            {p.status === 'closed' ? (
              <div className="note good">{p.chosen_date ? `Die Abstimmung ist abgeschlossen. Der Termin ist am ${fmtDate(p.chosen_date)}.` : 'Die Abstimmung ist abgeschlossen.'}</div>
            ) : saved ? (
              <>
                <div className="note good">Vielen Dank, Ihre Antwort ist gespeichert. {p.org_name} meldet sich mit dem festen Termin.</div>
                <button type="button" className="btn" onClick={() => setSaved(false)}>
                  Antwort ändern
                </button>
              </>
            ) : (
              <form
                className="login-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  send.mutate();
                }}
              >
                <MonthCalendar
                  start={firstMonth}
                  min={firstMonth}
                  max={lastMonth}
                  months={firstMonth === lastMonth ? 1 : 2}
                  enabled={(d) => offered.has(d)}
                  dayClass={(d) => (offered.has(d) ? `offer ${picked[d] ?? ''}` : '')}
                  dayTitle={(d) => (offered.has(d) ? (picked[d] === 'yes' ? 'möglich' : picked[d] === 'maybe' ? 'bedingt möglich' : 'vorgeschlagener Termin') : undefined)}
                  onDayClick={cycle}
                  onRange={range}
                />
                <div className="mcal-legend">
                  <span><i className="offer" />vorgeschlagener Termin</span>
                  <span><i className="yes" />möglich</span>
                  <span><i className="maybe" />bedingt möglich</span>
                </div>
                <TextArea label="Anmerkung (optional)" value={comment} onChange={setComment} rows={2} />
                {send.isError && <div className="note bad">{send.error instanceof ApiRequestError ? send.error.message : 'Speichern fehlgeschlagen'}</div>}
                <button className="btn primary login-btn" type="submit" disabled={send.isPending}>
                  {anyPicked ? 'Antwort senden' : 'Antwort senden: kein Termin möglich'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
